import { clearReadCache } from './utils/offlineReadCache';
// client/src/serviceWorkerRegistration.js
// Registers the runtime-caching service worker (public/service-worker.js) that
// provides the offline app shell and offline printing.
//
// Update flow (this is what previously forced the worker to be disabled): a new
// build is NEVER activated behind the user's back, and it is never left waiting
// forever either. When a new version finishes installing we surface a prompt; the
// user accepts, we tell the waiting worker to take over, and the resulting
// `controllerchange` reloads the page exactly once onto the fresh build.

let refreshing = false;
let waitingRegistration = null;

export const SW_UPDATE_EVENT = 'app:sw:update-available';

// The worker can't read REACT_APP_API_BASE_URL — that's baked into the app bundle at
// build time. Without being told, it can only pattern-match `/api/` paths, which misses
// the cross-origin backend routes that aren't under /api/ (/teacher/classes and friends)
// — i.e. most of the reference data needed offline. So announce the origin explicitly.
function announceApiOrigin(worker) {
  try {
    const configured = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    const origin = configured ? new URL(configured, window.location.origin).origin : window.location.origin;
    const target = worker || navigator.serviceWorker.controller;
    if (target) target.postMessage({ type: 'SET_API_ORIGIN', origin });
  } catch (_) { /* falls back to path matching */ }
}

function announceUpdate(registration) {
  waitingRegistration = registration;
  try {
    window.dispatchEvent(new CustomEvent(SW_UPDATE_EVENT, { detail: { registration } }));
  } catch (_) {}
}

export function register(callbacks = {}) {
  if (!('serviceWorker' in navigator)) return;
  // Service workers only run over https (or localhost).
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        // Tell the worker where the backend lives, both now and whenever a new
        // worker takes over (each fresh worker starts without this knowledge).
        announceApiOrigin(registration.active || navigator.serviceWorker.controller);
        navigator.serviceWorker.ready.then((r) => announceApiOrigin(r.active)).catch(() => {});

        // A build deployed while this tab was open and is already waiting.
        //
        // TAKEN, NOT OFFERED. This announced the update and left the new build
        // waiting for someone to accept it. Nobody did — six deploys in a row
        // sat queued behind that prompt while the browser kept serving the
        // cached bundle, and every one of them looked like a fix that had not
        // worked. A staged build that never activates is worse than no caching
        // at all, because it is invisible: the server has the new code, the
        // deploy is green, and the app is old.
        //
        // The prompt still fires for anything listening; it just no longer
        // decides whether the update happens.
        if (registration.waiting && navigator.serviceWorker.controller) {
          announceUpdate(registration);
          if (typeof callbacks.onUpdate === 'function') callbacks.onUpdate(registration);
          applyUpdate(registration);
        }

        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (installing.state !== 'installed') return;
            if (navigator.serviceWorker.controller) {
              // Updated content is staged — take it. See the note above: waiting
              // for consent is what made six deploys invisible.
              announceUpdate(registration);
              if (typeof callbacks.onUpdate === 'function') callbacks.onUpdate(registration);
              applyUpdate(registration);
            } else if (typeof callbacks.onSuccess === 'function') {
              // First install — content is now cached for offline use.
              callbacks.onSuccess(registration);
            }
          };
        };

        // Catch a deploy that lands while the tab stays open for hours.
        try { registration.update(); } catch (_) {}
        window.addEventListener('focus', () => {
          try { registration.update(); } catch (_) {}
        });
      })
      .catch((error) => {
        console.error('SW registration failed:', error);
      });

    // On the very first install the worker's clients.claim() also fires
    // `controllerchange`. Reloading then would bounce the page on a user's first
    // visit for no reason, so only treat it as an update if a worker was already
    // in control when this page loaded.
    const hadController = !!navigator.serviceWorker.controller;

    // When the active worker changes, reload once so the new assets take effect.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing || !hadController) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

// Activate the staged build. Triggers `controllerchange`, which reloads the page.
export function applyUpdate(registration) {
  const reg = registration || waitingRegistration;
  const worker = reg && (reg.waiting || reg.installing);
  if (!worker) {
    window.location.reload();
    return;
  }
  try {
    worker.postMessage('SKIP_WAITING');
  } catch (_) {
    window.location.reload();
  }
}

// Drop every cached response. Called at session boundaries (login page mount and
// explicit logout) so student PII and one school's data never persist on a shared
// device into the next session. Safe to call when no worker is installed.
export async function purgeOfflineCaches() {
  // Ask the worker first so it purges from its own scope...
  try {
    const registration = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : null;
    const worker = registration && (registration.active || navigator.serviceWorker.controller);
    if (worker) worker.postMessage({ type: 'PURGE_CACHES' });
  } catch (_) {}

  // ...and delete directly too, so the purge still happens when no worker is
  // controlling this page yet (first load after logout, hard refresh, etc.).
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_) {}

  // The API read cache is a separate store from the service worker's, and it
  // holds parsed responses — class lists, pupil records, fee ledgers. On a
  // shared school device it must go with the rest.
  //
  // Queued WRITES are deliberately left: they are tagged by owner, belong to
  // whoever made them, and that person may sign back in to send them.
  try { await clearReadCache(); } catch (_) {}
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => {
        console.error('SW unregister failed:', error);
      });
  }
}
